import { useState } from 'react';
import { assetDownloadUrl } from '@/lib/api';
import type { AssetWithTags } from '@/lib/types';
import PhotoLightbox from './PhotoLightbox';
import { FileText } from 'lucide-react';

/**
 * Square-tiled photo grid with click-to-open lightbox. PDFs render
 * a generic file icon with the original filename rather than an
 * <img> — keeps a survey-mixed-with-receipts grid coherent.
 */
export default function PhotoGrid({
  assets,
  emptyMessage = 'No photos yet.',
}: {
  assets: AssetWithTags[];
  emptyMessage?: string;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  if (assets.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
  }
  return (
    <>
      <ul
        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2"
        data-testid="photo-grid"
      >
        {assets.map((a) => (
          <li key={a.id}>
            <button
              type="button"
              onClick={() => setOpenId(a.id)}
              className="block w-full aspect-square rounded-md overflow-hidden border border-rule bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-shadow hover:shadow-md"
              data-testid={`photo-tile-${a.id}`}
            >
              {a.mimeType?.startsWith('image/') ? (
                <img
                  src={assetDownloadUrl(a.id)}
                  alt={a.caption ?? a.originalFilename ?? 'Photo'}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 text-muted-foreground">
                  <FileText className="h-8 w-8" aria-hidden />
                  <span className="font-mono text-[10px] uppercase tracking-wider px-2 text-center line-clamp-2">
                    {a.originalFilename ?? 'PDF'}
                  </span>
                </div>
              )}
            </button>
          </li>
        ))}
      </ul>
      {openId ? (
        <PhotoLightbox
          asset={assets.find((a) => a.id === openId) ?? null}
          onClose={() => setOpenId(null)}
        />
      ) : null}
    </>
  );
}
